import React, { useEffect, useState, useCallback } from 'react';
import { BACKEND_URL } from '../../config';
import '../../css/AdminTable.css';

const RATING_LABEL = { 5: 'Відмінно', 4: 'Добре', 3: 'Нормально', 2: 'Погано', 1: 'Дуже погано' };

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('uk-UA', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const Stars = ({ score }) => (
    <span style={{ color: '#FFC93C', fontSize: 14, letterSpacing: 1 }}>
        {'★'.repeat(score || 0)}{'☆'.repeat(Math.max(0, 5 - (score || 0)))}
    </span>
);

const ReviewCard = ({ review, onApprove, onDelete, approving, deleting }) => (
    <div style={{
        background: '#fff',
        border: review.approved ? '1px solid #c8e6c9' : '1px solid #ffe0b2',
        borderRadius: 10,
        padding: '18px 20px',
        position: 'relative',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
        {/* Status badge */}
        <span style={{
            position: 'absolute', top: 14, right: 14,
            fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '2px 8px',
            background: review.approved ? '#e6f9ec' : '#fff3e0',
            color: review.approved ? '#27a24f' : '#e65100',
        }}>
            {review.approved ? 'Опубліковано' : 'На модерації'}
        </span>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, paddingRight: 110 }}>
            <Stars score={review.ratingScore} />
            <span style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>
                {RATING_LABEL[review.ratingScore] || review.ratingText}
            </span>
            <span style={{ color: '#aaa', fontSize: 12, marginLeft: 'auto' }}>
                {formatDate(review.datetimeIso)}
            </span>
        </div>

        {/* Author */}
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
            {review.author || '—'}
        </div>

        {/* Product */}
        {review.productName && (
            <div style={{ fontSize: 13, color: '#777', marginBottom: 6 }}>
                🛒 {review.productName}
            </div>
        )}

        {/* Text */}
        {review.reviewText && (
            <div style={{
                fontSize: 13, color: '#444', lineHeight: 1.6,
                background: '#fafafa', borderRadius: 6, padding: '8px 12px', marginBottom: 8,
            }}>
                {review.reviewText}
            </div>
        )}

        {/* Tags */}
        {review.tags && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {review.tags.split(';').map(t => t.trim()).filter(Boolean).map(tag => (
                    <span key={tag} style={{
                        fontSize: 11, background: '#f0f2f5', color: '#555',
                        borderRadius: 20, padding: '2px 10px', fontWeight: 500,
                    }}>{tag}</span>
                ))}
            </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {!review.approved && (
                <button
                    onClick={() => onApprove(review.id)}
                    disabled={approving}
                    style={{
                        padding: '6px 16px', background: '#27a24f', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                        cursor: approving ? 'default' : 'pointer', opacity: approving ? 0.6 : 1,
                    }}
                >
                    {approving ? '...' : '✓ Опублікувати'}
                </button>
            )}
            <button
                onClick={() => onDelete(review.id)}
                disabled={deleting}
                style={{
                    padding: '6px 16px', background: '#d9534f', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                    cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.6 : 1,
                }}
            >
                {deleting ? '...' : 'Видалити'}
            </button>
        </div>
    </div>
);

const ReviewsAdmin = () => {
    const [tab, setTab] = useState('unapproved'); // 'unapproved' | 'all'
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionId, setActionId] = useState({ approve: null, delete: null });

    const token = localStorage.getItem('authToken');
    const authHeader = { 'Authorization': `Bearer ${token}` };

    const fetchReviews = useCallback(async (mode) => {
        setLoading(true);
        setError(null);
        const url = mode === 'unapproved'
            ? `${BACKEND_URL}/reviews/admin/unapproved`
            : `${BACKEND_URL}/reviews/admin/all`;
        try {
            const res = await fetch(url, { headers: authHeader });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setReviews(Array.isArray(data) ? data : (data.reviews || []));
        } catch (err) {
            setError(`Не вдалося завантажити відгуки: ${err.message}`);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line
    }, []);

    useEffect(() => { fetchReviews(tab); }, [fetchReviews, tab]);

    const handleApprove = async (id) => {
        setActionId(a => ({ ...a, approve: id }));
        try {
            const res = await fetch(`${BACKEND_URL}/reviews/admin/${id}/approve`, {
                method: 'PATCH',
                headers: authHeader,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            fetchReviews(tab);
        } catch (err) {
            setError(`Помилка при апруві: ${err.message}`);
        } finally {
            setActionId(a => ({ ...a, approve: null }));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Видалити цей відгук?')) return;
        setActionId(a => ({ ...a, delete: id }));
        try {
            const res = await fetch(`${BACKEND_URL}/reviews/admin/${id}`, {
                method: 'DELETE',
                headers: authHeader,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            fetchReviews(tab);
        } catch (err) {
            setError(`Помилка при видаленні: ${err.message}`);
        } finally {
            setActionId(a => ({ ...a, delete: null }));
        }
    };

    return (
        <div>
            <h2 style={{ marginBottom: 20 }}>Відгуки</h2>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #eee' }}>
                {[
                    { key: 'unapproved', label: 'На модерації' },
                    { key: 'all', label: 'Всі відгуки' },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            borderBottom: tab === key ? '2px solid #FFC93C' : '2px solid transparent',
                            background: 'none',
                            fontWeight: tab === key ? 700 : 400,
                            fontSize: 14,
                            color: tab === key ? '#212529' : '#6c757d',
                            cursor: 'pointer',
                            marginBottom: -2,
                            transition: 'color 0.12s',
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading && <div style={{ color: '#777', padding: '40px 0', textAlign: 'center' }}>Завантаження...</div>}
            {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

            {!loading && !error && reviews.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '40px 0', fontSize: 15 }}>
                    {tab === 'unapproved' ? 'Немає відгуків на модерації' : 'Відгуків ще немає'}
                </div>
            )}

            {!loading && !error && reviews.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {reviews.map(review => (
                        <ReviewCard
                            key={review.id}
                            review={review}
                            onApprove={handleApprove}
                            onDelete={handleDelete}
                            approving={actionId.approve === review.id}
                            deleting={actionId.delete === review.id}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewsAdmin;
